/**
 * @prop --width: Width of popover above small breakpoint
 * @prop --min-width: Minimum width of popover above small breakpoint
 * @prop --max-width: Maximum width of popover above small breakpoint
 * @prop --padding: Padding of popover
 * @prop --content-padding: Padding of popover content
 * @prop --border-radius: Border radius of popover
 * @prop --backdrop-opacity: Opacity of popover backdrop (used on mikro breakpoint)
 *
 * @prop --stzh-popover-width: **Global**: Width of popover above small breakpoint (default `223px`)
 * @prop --stzh-popover-min-width: **Global**: Minimum width of popover above small breakpoint (default `initial`)
 * @prop --stzh-popover-max-width: **Global**: Maximum width of popover above small breakpoint (default `initial`)
 * @prop --stzh-popover-padding: **Global**: Padding of popover (default `0px`)
 * @prop --stzh-popover-content-padding: **Global**: Padding of popover content (default `8px 0px`)
 * @prop --stzh-popover-border-radius: **Global**: Border radius of popover above small breakpoint (default `3px`)
 *
 * @prop --stzh-dialog-backdrop-opacity: **Global**: Opacity of backdrops
 * @prop --stzh-dialog-backdrop-background-color: **Global**: Background color of backdrops
 */

:host {
  display: contents;

  --color: #{$colorBlack};
  --width: #{$popoverWidth};
  --min-width: #{$popoverMinWidth};
  --max-width: #{$popoverMaxWidth};
  --height: auto;
  --min-height: initial;
  --max-height: initial;
  --padding: #{$popoverPadding};
  --content-padding: #{$popoverContentPadding};
  --border-radius: #{$popoverBorderRadius};
  --background-color: var(--stzh-popover-background, #{$popoverBackgroundColor});
  --backdrop-opacity: #{$dialogBackdropOpacity};
  --backdrop-background-color: #{$dialogBackdropBackgroundColor};

  &[size="large"] {
    --content-padding: #{$popoverLargeContentPadding};
  }

  &[variant="secondary"] {
    --background-color: #{$colorSecondary20};
  }

  &[variant="tooltip"] {
    --width: #{$tooltipWidth};
    --min-width: #{$tooltipMinWidth};
    --max-width: #{$tooltipMaxWidth};
    --content-padding: #{space("small")} #{space("large")};
    --border-radius: #{$tooltipBorderRadius};

    @include mq($from: small) {
      --content-padding: #{$tooltipPadding};
      --color: #{$colorWhite};
      --link-color: #{$colorWhite};
      --hover-link-color: #{$colorWhite70op};
      --background-color: #{$colorCoolgrey60};
    }
  }
}

.stzh-popover {
  @include tooltip-arrow;
  display: contents;

  &__trigger {
    display: contents;
  }

  &__content-wrapper {
    // position: absolute;
    // width: 100%;
    // height: 100%;
    // pointer-events: none;
    display: contents;

    @include mq($to: small) {
      z-index: $zIndexDialog;
      display: flex;
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      flex-direction: column;
      align-items: stretch;
      justify-content: stretch;
    }
  }

  &__backdrop {
    display: contents;

    @include mq($to: small) {
      z-index: 1;
      position: absolute;
      display: block;
      top: 0px;
      left: 0px;
      width: 100%;
      height: 100%;
      overflow-x: hidden;
      overflow-y: auto;
      text-align: center;
      background-color: var(--backdrop-background-color);
      opacity: var(--backdrop-opacity);
    }
  }

  &__content {
    // pointer-events: all;
    display: contents;

    @include mq($to: small) {
      z-index: 2;
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      flex-grow: 1;
      overflow: auto;
    }
  }

  &__dialog {
    margin-top: auto;
    width: 100%;
    overflow: visible;
    border-top-left-radius: 2px;
    border-top-right-radius: 2px;
    background-color: var(--background-color);
    touch-action: none;
    outline: none;
    border-bottom: 2px solid $colorOldGrey13;
    color: var(--color);

    --stzh-base-color: var(--color);
    --stzh-link-color: var(--link-color);
    // TODO: check with designer
    --stzh-link-hover-color: var(--hover-link-color);

    @include mq($from: small) {
      z-index: $zIndexPopover;
      position: absolute;
      display: flex;
      width: var(--width);
      min-width: var(--min-width);
      max-width: var(--max-width);
      // max-width: calc(100vw - var(--stzh-scrollbar-width) - #{space('small')});
      height: var(--height);
      min-height: var(--min-height);
      max-height: var(--max-height);
      left: auto;
      right: auto;
      bottom: auto;
      border-radius: var(--border-radius);
      box-shadow: $boxShadowPopover;
      border: none;
    }
  }

  &__content-inner,
  &__content-slot-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  &__content-slot-wrapper {
    padding: var(--padding);
  }

  &__content-slot {
    overflow-y: auto;
    overflow-x: hidden;
    overflow-scrolling: touch;
    padding: var(--content-padding);
  }

  &__action-slot {
    display: flex;
    flex-direction: column;

    // &:not(:empty) {
    //   margin: space('xsmall') space('small');
    //   margin-bottom: space('medium');
    // }
  }

  &__label-wrapper {
    display: flex;
    align-items: center;
    padding: space("small") space("large");
    padding-right: space("medium");
    border-bottom: 1px solid $baseBorderColor;
    color: $colorOldGrey70;

    @include mq($from: small) {
      padding: 0;
      border-bottom: 0;
    }
  }

  &__label {
    @include font("heavy");
    @include fontCurve("p2");
    margin: 0;
    margin-right: space("small");

    @include mq($from: small) {
      @include visuallyhidden;
    }
  }

  &__close {
    visibility: $popoverCloseVisibility;
    cursor: pointer;
    display: flex;
    appearance: none;
    font-family: inherit;
    border: none;
    background-color: transparent;
    padding: 0;
    margin-left: auto;
    border-radius: 50%;
    color: $baseLeadColor;
    padding: space("xxsmall");
  }

  &__icon {
    --size: #{iconSize("xsmall")};
  }

  &__arrow {
    position: absolute;
    display: none;

    @include mq($from: small) {
      display: flex;
    }
  }

  &__vhidden {
    @include visuallyhidden;
  }

  /* Only non tooltip variants */

  &--default &__close,
  &--secondary &__close {
    &:focus {
      box-shadow: 0 0 0 1px $colorPrimary;
    }

    @include mq($from: small) {
      @include visuallyhiddenFocusable;
      position: absolute;
      top: 0;
      right: 0;
      transform: translate(30%, -30%);
      background-color: $colorOldGrey5;
    }
  }

  /* Tooltip variant */

  &--tooltip &__content-inner {
    @include mq($from: small) {
      flex-direction: row-reverse;
      align-items: flex-start;
    }
  }

  &--tooltip &__close {
    visibility: visible;

    @include mq($from: small) {
      @include tooltip__close;
    }
  }

  &--tooltip &__icon {
    @include mq($from: small) {
      @include tooltip__icon;
    }
  }

  &--tooltip &__dialog {
    @include mq($from: small) {
      @include tooltip-dropshadow;
    }
  }

  &--tooltip &__content-slot {
    @include mq($from: small) {
      @include tooltip-fontsize;
    }
  }

  /* Has fullwidth popover */

  &--popover-fullwidth-horizontal &__dialog {
    @include mq($from: small) {
      left: 0 !important;
      right: 0 !important;
      width: 100%;
    }
  }

  &--popover-fullwidth-vertical &__dialog {
    @include mq($from: small) {
      top: 0 !important;
      bottom: 0 !important;
      height: 100%;
    }
  }
}
